import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { DebtBlockData } from '@foco/contracts';
import { formatCurrency } from '../../../shared/lib/format';
import { useLocale } from './primitives';

/**
 * Bank debt and rejected cheques (Argentina).
 *
 * The strongest risk signal available for the country and one of very few in the
 * region: it is what a company actually owes the banking system. Two facts are
 * shown separately because they mean different things — outstanding debt is
 * common and often benign, while a *rejected cheque* means a payment was refused
 * for insufficient funds, which is much more specific.
 */
export function DebtBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();
  const locale = useLocale();
  const block = (data ?? {}) as DebtBlockData;
  const rows = block.entities ?? [];
  const hasDebt = (block.totalAmount ?? 0) > 0 || rows.length > 0;

  return (
    <div className="space-y-3">
      {hasDebt ? (
        <div className="flex items-baseline justify-between gap-3">
          <p className="text-sm font-medium">
            {t('blocks.debt.total')}:{' '}
            {formatCurrency(block.totalAmount ?? 0, block.currency ?? 'ARS', locale)}
          </p>
          {block.worstSituation && (
            <span className="rounded-full bg-risk-medium/10 px-2 py-0.5 text-xs font-medium text-risk-medium">
              {t('blocks.debt.situation')}: {block.worstSituation}
            </span>
          )}
        </div>
      ) : (
        <p className="text-sm text-risk-low">{t('blocks.debt.empty')}</p>
      )}

      {/* Rejected cheques get their own line regardless of the debt figure. */}
      <p
        className={
          block.hasRejectedCheques
            ? 'flex items-center gap-1.5 text-sm text-risk-high'
            : 'flex items-center gap-1.5 text-sm text-risk-low'
        }
      >
        {block.hasRejectedCheques ? (
          <AlertTriangle aria-hidden className="h-4 w-4" />
        ) : (
          <CheckCircle2 aria-hidden className="h-4 w-4" />
        )}
        {t('blocks.debt.rejectedCheques')}:{' '}
        {block.hasRejectedCheques ? t('pricing.yes') : t('pricing.no')}
      </p>

      {rows.length > 0 && (
        <ul className="divide-y divide-[var(--color-border)] text-sm">
          {rows.slice(0, 8).map((row, index) => (
            <li key={index} className="flex items-baseline justify-between gap-3 py-1.5">
              <span className="min-w-0 truncate">{row.institution ?? '—'}</span>
              <span className="shrink-0 font-mono text-xs">
                {row.amount === null
                  ? '—'
                  : formatCurrency(row.amount, block.currency ?? 'ARS', locale)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
